"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RivalryStandings = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const material_1 = require("@mui/material");
const LeaderboardContext_1 = require("../Context/LeaderboardContext");
const LeaderboardViewPaper_1 = require("./LeaderboardViewPaper");
const RivalryLogicFunctions_1 = require("../Functions/RivalryLogicFunctions");
const rivalryConfigUtils_1 = require("../Functions/rivalryConfigUtils");
const stablefordUtils_1 = require("../Functions/stablefordUtils");
const courseDataHelpers_1 = require("../Utils/courseDataHelpers");
const LeaderBoardPairs_1 = require("./LeaderBoardPairs");
const ScrollableFadeOverlay_1 = require("./ScrollableFadeOverlay");
const individualLeaderboardGridSx_1 = require("./StackStyles/individualLeaderboardGridSx");
// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatScore = (toPar, totalPoints, isStableford) => {
    if (isStableford)
        return totalPoints !== undefined ? `${totalPoints} pts` : '-';
    if (toPar === undefined)
        return '-';
    if (toPar === 0)
        return 'E';
    return toPar > 0 ? `+${toPar}` : `${toPar}`;
};
const getSinglePlayerInitials = (name) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2)
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
};
/** Initials for one or more players; multi-player entries joined with `|`. */
const getInitials = (playerInfo) => {
    if (!playerInfo?.length)
        return '??';
    return playerInfo
        .map((player) => getSinglePlayerInitials(player.playerName ?? ''))
        .join(' | ');
};
const VS_INITIALS_TEXT_SHADOW = '0 1px 2px rgba(0,0,0,0.55), 0 0 1px rgba(0,0,0,0.85)';
const LEAD_REF = 30;
const CHIP_SCALE = 1.2;
function computeSplit(leadAmount, teamAIsLeader) {
    if (!teamAIsLeader && teamAIsLeader !== false) {
        return { teamASplit: 0.5, leanDir: 0 };
    }
    const fraction = Math.min(leadAmount / LEAD_REF, 0.5);
    const extra = fraction / 2;
    const teamASplit = 0.5 + (teamAIsLeader ? extra : -extra);
    const leanDir = leadAmount === 0 ? 0 : teamAIsLeader ? 1 : -1;
    return { teamASplit, leanDir };
}
const RivalrySplitPill = ({ teamAScore, teamBScore, teamAColor, teamBColor, teamASplit, leanDir, factor, height = 70, }) => {
    const pillRef = (0, react_1.useRef)(null);
    const [pillWidth, setPillWidth] = (0, react_1.useState)(0);
    const pillHeight = height * factor;
    (0, react_1.useLayoutEffect)(() => {
        const el = pillRef.current;
        if (!el)
            return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setPillWidth(entry.contentRect.width);
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    const diagonalOffset = pillWidth > 0 ? (Math.tan((30 * Math.PI) / 180) * pillHeight) / 2 / pillWidth : 0;
    const topRight = (teamASplit + leanDir * diagonalOffset) * 100;
    const bottomRight = (teamASplit - leanDir * diagonalOffset) * 100;
    const clipPath = `polygon(0 0, ${topRight}% 0, ${bottomRight}% 100%, 0 100%)`;
    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { ref: pillRef, sx: {
            borderRadius: 999,
            overflow: 'hidden',
            position: 'relative',
            height: pillHeight,
            width: '100%',
            border: '4px solid',
            borderColor: 'secondary.main',
            boxShadow: '0 0 14px rgba(0,0,0,0.22), 0 0 6px rgba(0,0,0,0.12)',
        }, children: [(0, jsx_runtime_1.jsx)(material_1.Box, { sx: { position: 'absolute', inset: 0, backgroundColor: teamBColor } }), (0, jsx_runtime_1.jsx)(material_1.Box, { sx: { position: 'absolute', inset: 0, backgroundColor: teamAColor, clipPath } }), (0, jsx_runtime_1.jsxs)(material_1.Box, { sx: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                            position: 'absolute',
                            left: '18%',
                            transform: 'translateX(-50%)',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: 22 * factor,
                            textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                            whiteSpace: 'nowrap',
                        }, children: teamAScore }), (0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                            position: 'absolute',
                            left: '82%',
                            transform: 'translateX(-50%)',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: 22 * factor,
                            textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                            whiteSpace: 'nowrap',
                        }, children: teamBScore })] })] }));
};
const RivalryStandings = ({ leaderboardData, courseData, roundValue, stackRef }) => {
    const { factor, appTheme, standingTabs, windowSize, leaderboardScoreFormat, isPWA, isHeadless, hideSelector, eventData, bottomPadding, } = (0, LeaderboardContext_1.useLeaderboardContext)();
    const [paperPosition, setPaperPosition] = (0, react_1.useState)({ top: 0 });
    const [fadeIn, setFadeIn] = (0, react_1.useState)(false);
    const [selectedPanel, setSelectedPanel] = (0, react_1.useState)('vs');
    const panelRef = (0, react_1.useRef)(null);
    const rivalry = (0, react_1.useMemo)(() => (0, rivalryConfigUtils_1.resolveRivalryConfig)(eventData), [
        eventData?.useRivalry,
        eventData?.rivalry?.method,
        eventData?.rivalry?.teamA?.name,
        eventData?.rivalry?.teamA?.color,
        eventData?.rivalry?.teamB?.name,
        eventData?.rivalry?.teamB?.color,
        eventData?.rivalry?.rivalrySize,
        eventData?.rivalry?.countingScores,
    ]);
    const roundPlayType = eventData?.rounds?.[roundValue - 1]?.playType;
    const isStableford = (0, stablefordUtils_1.isStablefordPlayType)(roundPlayType);
    const localRivalryData = (0, react_1.useMemo)(() => {
        if (leaderboardData.length === 0 || !rivalry || standingTabs !== 'rivalry')
            return null;
        return (0, RivalryLogicFunctions_1.computeRivalryResult)(rivalry.method ?? 'summation', {
            leaderboardData,
            courseData,
            currentRound: roundValue,
            leaderboardScoreFormat,
            eventData,
        });
    }, [leaderboardData, courseData, standingTabs, leaderboardScoreFormat, roundValue, rivalry, eventData]);
    (0, react_1.useEffect)(() => {
        if (standingTabs === 'rivalry')
            setFadeIn(true);
        else
            setFadeIn(false);
    }, [standingTabs]);
    (0, react_1.useEffect)(() => {
        const timeoutId = setTimeout(() => {
            const dividerElement = document.getElementById('leaderboard-button-divider');
            if (dividerElement) {
                const rect = dividerElement.getBoundingClientRect();
                const topOffset = hideSelector ? 0 : (isHeadless ? 60 : (windowSize.appBarHeight ?? 70));
                setPaperPosition({ top: rect.bottom - topOffset + 5 * factor * 1.5 });
            }
        }, 0);
        return () => clearTimeout(timeoutId);
    }, [standingTabs, factor, isHeadless, hideSelector, windowSize.appBarHeight]);
    // ── Derived values ──────────────────────────────────────────────────────
    const teamA = localRivalryData?.teamA ?? null;
    const teamB = localRivalryData?.teamB ?? null;
    const leader = localRivalryData?.leader ?? null;
    const leadAmount = localRivalryData?.leadAmount ?? 0;
    const teamAColor = rivalry?.teamA?.color ?? '#1976d2';
    const teamBColor = rivalry?.teamB?.color ?? '#e53935';
    const teamAIsLeader = leader !== null ? leader === rivalry?.teamA?.name : null;
    const { teamASplit, leanDir } = computeSplit(leadAmount, teamAIsLeader);
    const teamAScore = formatScore(teamA?.toPar, teamA?.totalPoints, isStableford);
    const teamBScore = formatScore(teamB?.toPar, teamB?.totalPoints, isStableford);
    // ── Course data for team panels ─────────────────────────────────────────
    const roundCourseID = eventData?.rounds?.[roundValue - 1]?.courseID;
    const currentCourseData = (0, react_1.useMemo)(() => (0, courseDataHelpers_1.getCourseById)(courseData, roundCourseID), [courseData, roundCourseID]);
    // ── Team panel players (sorted, with within-team rank) ──────────────────
    const getTeamPlayers = (0, react_1.useMemo)(() => {
        return (teamName) => {
            const members = leaderboardData.filter((item) => (item.rivalryTeam ?? item.playerInfo?.[0]?.rivalryTeam) === teamName);
            const sorted = [...members].sort(isStableford
                ? (a, b) => (b.totalPoints ?? 0) - (a.totalPoints ?? 0)
                : (a, b) => (a.toPar ?? 0) - (b.toPar ?? 0));
            let currentRank = 1;
            return sorted.map((item, i) => {
                if (i > 0) {
                    const prev = isStableford ? sorted[i - 1].totalPoints : sorted[i - 1].toPar;
                    const curr = isStableford ? item.totalPoints : item.toPar;
                    if (curr !== prev)
                        currentRank = i + 1;
                }
                return { ...item, rank: currentRank };
            });
        };
    }, [leaderboardData, isStableford]);
    const teamAPlayers = (0, react_1.useMemo)(() => getTeamPlayers(rivalry?.teamA?.name ?? ''), [getTeamPlayers, rivalry?.teamA?.name]);
    const teamBPlayers = (0, react_1.useMemo)(() => getTeamPlayers(rivalry?.teamB?.name ?? ''), [getTeamPlayers, rivalry?.teamB?.name]);
    // ── VS panel: per-player round scores ──────────────────────────────────
    const vsPlayerData = (0, react_1.useMemo)(() => {
        if (!rivalry)
            return { teamA: [], teamB: [] };
        const isNet = leaderboardScoreFormat === 'net';
        const holePoints = eventData?.rounds?.[roundValue - 1]?.holePoints ?? [];
        const processTeam = (teamName) => {
            const members = leaderboardData.filter((item) => (item.rivalryTeam ?? item.playerInfo?.[0]?.rivalryTeam) === teamName);
            const scored = members.map((item) => {
                const teeSetData = (0, courseDataHelpers_1.getTeeSetFromCourse)(currentCourseData, item.playerInfo?.[0]?.teeSet);
                let toPar = 0;
                let totalPoints = 0;
                (item.roundScores?.[roundValue - 1]?.holeScores ?? []).forEach((h) => {
                    if (h.playerScore !== 0 && h.playerScore !== null) {
                        toPar += h.playerScore
                            - (isNet ? (h.playerHcpAdj ?? 0) : 0)
                            - (teeSetData?.holes?.[h.holeNumber - 1]?.par ?? 0);
                        if (isStableford) {
                            totalPoints += (0, stablefordUtils_1.calculateHoleStablefordPoints)(h.playerScore, h.par, h.playerHcpAdj ?? 0, isNet, holePoints);
                        }
                    }
                });
                return { playerInfo: item.playerInfo ?? [], toPar, totalPoints };
            });
            return isStableford
                ? scored.sort((a, b) => b.totalPoints - a.totalPoints)
                : scored.sort((a, b) => a.toPar - b.toPar);
        };
        return {
            teamA: processTeam(rivalry.teamA.name),
            teamB: processTeam(rivalry.teamB.name),
        };
    }, [leaderboardData, currentCourseData, roundValue, rivalry, leaderboardScoreFormat, isStableford, eventData]);
    const chipBaseSx = {
        fontSize: 11 * factor * CHIP_SCALE,
        height: 24 * factor * CHIP_SCALE,
        '& .MuiChip-label': { px: 1 * CHIP_SCALE },
    };
    // ── Chip toggle handler ─────────────────────────────────────────────────
    const handleChip = (panel) => {
        setSelectedPanel((prev) => (prev === panel ? 'none' : panel));
    };
    // ── Panel renderer ──────────────────────────────────────────────────────
    const renderTeamPanelHeader = () => ((0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
            ...individualLeaderboardGridSx_1.individualLeaderboardHeaderGridSx,
            ...individualLeaderboardGridSx_1.individualLeaderboardRowWidthSx,
            px: 0.5,
            py: 0.5,
            flexShrink: 0,
            backgroundColor: 'sepia.background',
            borderBottom: `1px solid ${appTheme.palette.divider}`,
        }, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { fontWeight: "500", variant: "caption", sx: { textAlign: 'center' }, children: "Rank" }), (0, jsx_runtime_1.jsx)(material_1.Typography, { fontWeight: "500", variant: "caption", sx: { textAlign: 'left' }, children: "Player" }), (0, jsx_runtime_1.jsx)(material_1.Typography, { fontWeight: "500", variant: "caption", sx: { textAlign: 'center' }, children: "Thru" }), (0, jsx_runtime_1.jsx)(material_1.Typography, { fontWeight: "500", variant: "caption", sx: { textAlign: 'center' }, children: isStableford ? 'Points' : 'Score' })] }));
    const renderTeamPanelRows = (players, teamColor) => players.map((score, index) => {
        const isCounting = index < (rivalry?.countingScores ?? players.length);
        return ((0, jsx_runtime_1.jsx)(material_1.Stack, { sx: {
                width: '98%',
                borderRadius: 1,
                borderLeft: `3px solid ${isCounting ? teamColor : 'transparent'}`,
                boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                mb: 0.5,
            }, children: (0, jsx_runtime_1.jsx)(LeaderBoardPairs_1.LeaderboardPairs, { scoreData: score, roundData: score.roundScores?.[roundValue - 1], index: index, teeSetData: (0, courseDataHelpers_1.getTeeSetFromCourse)(currentCourseData, score?.playerInfo?.[0]?.teeSet) }) }, score._id?.toString() ?? index));
    });
    const renderVsPlayerChip = (player, teamColor, isCounting) => ((0, jsx_runtime_1.jsx)(material_1.Chip, { label: getInitials(player.playerInfo), size: "small", sx: {
            fontSize: 13 * factor,
            height: 32 * factor,
            width: 15 + 8 * getInitials(player.playerInfo).length,
            '& .MuiChip-label': {
                px: 1.25,
                whiteSpace: 'nowrap',
                textShadow: VS_INITIALS_TEXT_SHADOW,
            },
            fontWeight: isCounting ? 600 : 400,
            backgroundColor: isCounting ? teamColor : appTheme.palette.grey[400],
            color: 'white',
        } }));
    const renderVsPlayerScore = (player, teamColor, isCounting) => ((0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "body2", sx: {
            fontWeight: isCounting ? 700 : 400,
            fontSize: 26 * factor,
            color: isCounting ? teamColor : 'text.secondary',
            textAlign: 'center',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
        }, children: formatScore(player.toPar, player.totalPoints, isStableford) }));
    const renderVsPanelRows = () => {
        const countingN = rivalry?.countingScores ?? 1;
        const rowCount = Math.max(vsPlayerData.teamA.length, vsPlayerData.teamB.length);
        const rowHeight = 32 * factor;
        return ((0, jsx_runtime_1.jsx)(material_1.Stack, { spacing: 1.5, sx: {
                width: '80%',
                alignSelf: 'center',
                px: 1,
                pt: 1,
            }, children: Array.from({ length: rowCount }, (_, i) => {
                const playerA = vsPlayerData.teamA[i];
                const playerB = vsPlayerData.teamB[i];
                const isCountingA = i < countingN;
                const isCountingB = i < countingN;
                return ((0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
                        position: 'relative',
                        width: '100%',
                        height: rowHeight,
                    }, children: [playerA && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                                position: 'absolute',
                                right: '80%',
                                top: '50%',
                                transform: 'translateY(-50%)',
                            }, children: renderVsPlayerChip(playerA, teamAColor, isCountingA) })), playerA && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                                position: 'absolute',
                                left: '30%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                            }, children: renderVsPlayerScore(playerA, teamAColor, isCountingA) })), playerB && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                                position: 'absolute',
                                left: '70%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                            }, children: renderVsPlayerScore(playerB, teamBColor, isCountingB) })), playerB && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                                position: 'absolute',
                                left: '80%',
                                top: '50%',
                                transform: 'translateY(-50%)',
                            }, children: renderVsPlayerChip(playerB, teamBColor, isCountingB) }))] }, `vs-row-${i}`));
            }) }));
    };
    // ── Render ──────────────────────────────────────────────────────────────
    return ((0, jsx_runtime_1.jsxs)(LeaderboardViewPaper_1.LeaderboardViewPaper, { viewType: "rivalry", standingTabs: standingTabs, paperPosition: paperPosition, fadeIn: fadeIn, windowSize: windowSize, isPWA: isPWA, factor: factor, children: [(0, jsx_runtime_1.jsxs)(material_1.Box, { sx: { width: '100%', flexShrink: 0 }, children: [eventData?.eventCardImageURL && ((0, jsx_runtime_1.jsx)(material_1.Box, { component: "img", src: eventData.eventCardImageURL, alt: "Event", sx: {
                            display: 'block',
                            margin: '0 auto',
                            maxHeight: 120,
                            maxWidth: '100%',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                            borderRadius: '4px 4px 0 0',
                        } })), (0, jsx_runtime_1.jsxs)(material_1.Stack, { direction: "column", spacing: 1.5, sx: { px: 1.5, pt: 1.5, pb: 0.5 }, children: [(0, jsx_runtime_1.jsx)(RivalrySplitPill, { teamAScore: teamAScore, teamBScore: teamBScore, teamAColor: teamAColor, teamBColor: teamBColor, teamASplit: teamASplit, leanDir: leanDir, factor: factor }), (0, jsx_runtime_1.jsxs)(material_1.Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", sx: { width: '80%', alignSelf: 'center', pb: 0.5 }, children: [(0, jsx_runtime_1.jsx)(material_1.Chip, { label: rivalry?.teamA?.name ?? 'Team A', size: "small", variant: selectedPanel === 'teamA' ? 'filled' : 'outlined', onClick: () => handleChip('teamA'), sx: {
                                            ...chipBaseSx,
                                            fontWeight: 600,
                                            borderColor: teamAColor,
                                            ...(selectedPanel === 'teamA'
                                                ? { backgroundColor: teamAColor, color: 'white', '&:hover': { backgroundColor: teamAColor } }
                                                : { color: teamAColor }),
                                        } }), (0, jsx_runtime_1.jsx)(material_1.Chip, { label: "VS", size: "small", variant: selectedPanel === 'vs' ? 'filled' : 'outlined', color: "primary", onClick: () => handleChip('vs'), sx: {
                                            ...chipBaseSx,
                                            fontWeight: 700,
                                            ...(selectedPanel === 'vs'
                                                ? { backgroundColor: 'primary.main', color: 'white', '&:hover': { backgroundColor: 'primary.main' } }
                                                : {}),
                                        } }), (0, jsx_runtime_1.jsx)(material_1.Chip, { label: rivalry?.teamB?.name ?? 'Team B', size: "small", variant: selectedPanel === 'teamB' ? 'filled' : 'outlined', onClick: () => handleChip('teamB'), sx: {
                                            ...chipBaseSx,
                                            fontWeight: 600,
                                            borderColor: teamBColor,
                                            ...(selectedPanel === 'teamB'
                                                ? { backgroundColor: teamBColor, color: 'white', '&:hover': { backgroundColor: teamBColor } }
                                                : { color: teamBColor }),
                                        } })] })] })] }), selectedPanel !== 'none' && ((0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
                    position: 'relative',
                    width: '100%',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                }, children: [(selectedPanel === 'teamA' || selectedPanel === 'teamB') && renderTeamPanelHeader(), (0, jsx_runtime_1.jsxs)(material_1.Stack, { ref: panelRef, direction: "column", spacing: 0, alignItems: "center", sx: {
                            width: '100%',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            flex: 1,
                            minHeight: 0,
                            paddingBottom: (0, LeaderboardContext_1.standingsScrollPaddingBottom)(bottomPadding),
                            WebkitOverflowScrolling: 'touch',
                        }, children: [selectedPanel === 'teamA' && renderTeamPanelRows(teamAPlayers, teamAColor), selectedPanel === 'teamB' && renderTeamPanelRows(teamBPlayers, teamBColor), selectedPanel === 'vs' && renderVsPanelRows()] }), (0, jsx_runtime_1.jsx)(ScrollableFadeOverlay_1.ScrollableFadeOverlay, { stackRef: panelRef, dataDependency: leaderboardData })] }))] }));
};
exports.RivalryStandings = RivalryStandings;
